import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/styles';
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import ChangesDelayedWarning from '../ChangesDelayedWarning/ChangesDelayedWarning';
import { useConfigureHeartbeatSyncMutation } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    marginBottom: theme.spacing(2),
  },
}));

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;
  onClose?(): void;
  onCreated?(): void;
}

const ConfigureSyncDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen, onClose, onCreated }) => {
    const classes = useStyles();
    const config = useConfig();
    const { authUser } = useAuthUser();
    const [configureSync, { isLoading }] = useConfigureHeartbeatSyncMutation();
    const [scheduledSyncEnabled, setScheduledSyncEnabled] = useState(true);
    const [timerDurationDays, setTimerDurationDays] = useState(0);
    const [timerDurationHours, setTimerDurationHours] = useState(0);
    const [timerDurationMinutes, setTimerDurationMinutes] = useState(0);
    const [retryCount, setRetryCount] = useState(0);
    const [retryCountTimeDays, setRetryCountTimeDays] = useState(0);
    const [retryCountTimeHours, setRetryCountTimeHours] = useState(0);
    const [retryCountTimeMinutes, setRetryCountTimeMinutes] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
      }
    }, [onCreated]);

    useEffect(() => {
      if (isOpen && devices.length > 0) {
        const td = devices[0].heartbeatTimerDuration || 0;
        const rc = devices[0].heartbeatRetryCount || 0;
        const rct = devices[0].heartbeatRetryCountTime || 0;
        setScheduledSyncEnabled(td > 0);
        setTimerDurationDays(Math.floor(td / (60 * 24)));
        setTimerDurationHours(Math.floor((td % (60 * 24)) / 60));
        setTimerDurationMinutes(Math.floor((td % 60) % 60));
        setRetryCount(rc);
        setRetryCountTimeDays(Math.floor(rct / (60 * 24)));
        setRetryCountTimeHours(Math.floor((rct % (60 * 24)) / 60));
        setRetryCountTimeMinutes(Math.floor((rct % 60) % 60));
      }
    }, [devices, isOpen]);

    const handleSubmit = async () => {
      const timerDuration = scheduledSyncEnabled
        ? timerDurationDays * 24 * 60 +
          timerDurationHours * 60 +
          timerDurationMinutes
        : 0;
      const retryCountTime = scheduledSyncEnabled
        ? retryCountTimeDays * 24 * 60 +
          retryCountTimeHours * 60 +
          retryCountTimeMinutes
        : 0;

      if (scheduledSyncEnabled && timerDuration > 0 && timerDuration < 360) {
        enqueueSnackbar('Minimum sync is 6 hours', { variant: 'warning' });
        return;
      }
      const resp: any = await configureSync({
        apiBaseURL: config.apiBaseURL,
        orgIDs: authUser.selectedOrgs,
        devices,
        timerDuration,
        retryCount: scheduledSyncEnabled ? retryCount : 0,
        retryCountTime,
      });
      if (!resp.error) {
        handleClose();
        handleCreated();
        return;
      }
      enqueueSnackbar(parseErrorMessage(resp.error), { variant: 'error' });
    };

    return (
      <>
        <Dialog
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Configure Sync</DialogTitle>
          <DialogContent>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduledSyncEnabled}
                  onChange={(e) => {
                    setScheduledSyncEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setTimerDurationDays(0);
                      setTimerDurationHours(0);
                      setTimerDurationMinutes(0);
                      setRetryCount(0);
                      setRetryCountTimeDays(0);
                      setRetryCountTimeHours(0);
                      setRetryCountTimeMinutes(0);
                    }
                  }}
                  color="primary"
                />
              }
              label="Enable Scheduled Sync Timer"
            />
            <br />
            <br />
            <Typography variant="body1">Sync Interval</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  id="timerDurationDays"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="days"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 0 }}
                  value={timerDurationDays.toString()}
                  onChange={(e) => {
                    setTimerDurationDays(
                      parseInt(e.currentTarget.value, 10) || 0
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="timerDurationHours"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="hours"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 6, max: 23 }}
                  value={timerDurationHours.toString()}
                  onChange={(e) => {
                    setTimerDurationHours(
                      parseInt(e.currentTarget.value, 10) || 0
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="timerDurationMinutes"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="minutes"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 0, max: 59 }}
                  value={timerDurationMinutes.toString()}
                  onChange={(e) => {
                    setTimerDurationMinutes(
                      parseInt(e.currentTarget.value, 10) || 0
                    );
                  }}
                />
              </Grid>
            </Grid>
            <br />
            <Typography variant="body1">Sync Retry Count</Typography>
            <Grid container spacing={2}>
              <Grid item>
                <TextField
                  id="retryCount"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="times"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 0 }}
                  value={retryCount.toString()}
                  onChange={(e) => {
                    setRetryCount(parseInt(e.currentTarget.value, 10) || 0);
                  }}
                />
              </Grid>
            </Grid>
            <br />
            <Typography variant="body1">Sync Retry Interval</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  id="retryCountTimeDays"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="days"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 0 }}
                  value={retryCountTimeDays.toString()}
                  onChange={(e) => {
                    setRetryCountTimeDays(
                      parseInt(e.currentTarget.value, 10) || 0
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="retryCountTimeHours"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="hours"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 0, max: 23 }}
                  value={retryCountTimeHours.toString()}
                  onChange={(e) => {
                    setRetryCountTimeHours(
                      parseInt(e.currentTarget.value, 10) || 0
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="retryCountTimeMinutes"
                  type="number"
                  margin="dense"
                  variant="outlined"
                  helperText="minutes"
                  className={classes.input}
                  fullWidth
                  disabled={!scheduledSyncEnabled}
                  inputProps={{ min: 0, max: 59 }}
                  value={retryCountTimeMinutes.toString()}
                  onChange={(e) => {
                    setRetryCountTimeMinutes(
                      parseInt(e.currentTarget.value, 10) || 0
                    );
                  }}
                />
              </Grid>
            </Grid>
            <br />
            <ChangesDelayedWarning />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              onClick={handleSubmit}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default ConfigureSyncDialog;
